import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
 ArrowLeft,
 Package,
 User,
 DollarSign,
 Star,
 Trash2,
 ExternalLink,
 Loader2,
 AlertCircle,
 CheckCircle,
 XCircle,
 Tag,
 ListChecks,
} from 'lucide-react';
import { Button, PillToggle } from '../../../components/shared';
import { useToastContext } from '../../../contexts/ToastContext';
import {
 useAdminVendorService,
 useToggleVendorServiceStatus,
 useToggleVendorServiceFeatured,
 useDeleteVendorService,
} from '../../../services/adminVendorServiceService';
import { AdminPageHeader, AdminGuideCallout } from '../../../components/admin/guidance';

const money = (n) => {
 const v = Number(n);
 if (Number.isNaN(v)) return '—';
 return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(v);
};

const AdminVendorServiceDetails = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const { success, error: showError } = useToastContext();

 const { data, isLoading, error, refetch } = useAdminVendorService(id);
 const service = data?.data;

 const toggleStatusMutation = useToggleVendorServiceStatus({
 onSuccess: (res) => success(res?.message || 'Service status updated'),
 onError: (err) => showError(err?.response?.data?.message || err?.message || 'Failed to update status'),
 });

 const toggleFeaturedMutation = useToggleVendorServiceFeatured({
 onSuccess: (res) => success(res?.message || 'Featured status updated'),
 onError: (err) => showError(err?.response?.data?.message || err?.message || 'Failed to update featured'),
 });

 const deleteMutation = useDeleteVendorService({
 onSuccess: () => {
 success('Service deleted');
 navigate('/admin/services');
 },
 onError: (err) => showError(err?.response?.data?.message || err?.message || 'Failed to delete service'),
 });

 if (isLoading) {
 return (
 <div className="py-24 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-gray-500">Loading service…</p>
 </div>
 );
 }

 if (error || !service) {
 return (
 <div className="rounded-lg border border-red-200 bg-red-50/80 p-8 text-center">
 <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
 <h3 className="text-lg font-medium text-red-800 mb-2">Service not found</h3>
 <p className="text-sm text-red-600 mb-4">
 {error?.response?.data?.message || error?.message || 'Could not load this service.'}
 </p>
 <div className="flex justify-center gap-2">
 <Button className="rounded-md" variant="secondary" onClick={() => navigate('/admin/services')}>
 Back to services
 </Button>
 <Button className="rounded-md" onClick={() => refetch()}>
 Retry
 </Button>
 </div>
 </div>
 );
 }

 const vendorUser = service.vendor_profile?.user;
 const vendorProfile = service.vendor_profile;
 const packages = service.vendor_service_packages || service.vendorServicePackages || [];

 return (
 <div className="space-y-5 max-w-5xl">
 <AdminPageHeader
 title={service.service_name || 'Service'}
 subtitle="Review service details, packages, and featured / active controls."
 actions={
 <Link
 to="/admin/services"
 className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 <ArrowLeft className="w-4 h-4" />
 All services
 </Link>
 }
 />

 <AdminGuideCallout
 title="Featured service vs featured vendor"
 steps={[
 'Featured service highlights this offering in browse/category results and helps the vendor enter the homepage featured pool.',
 'Featured vendor (on the vendor profile) is the explicit homepage spotlight flag.',
 'Turning Featured on here also marks the vendor featured so the public homepage can show them.',
 ]}
 icon={ListChecks}
 />

 <div className="product-panel p-4 sm:p-5">
 <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
 <div className="flex items-start gap-4 min-w-0">
 <div className="h-14 w-14 rounded-lg bg-gray-900 text-white flex items-center justify-center shrink-0">
 <Package className="w-7 h-7" />
 </div>
 <div className="min-w-0">
 <div className="flex flex-wrap items-center gap-2">
 <h2 className="text-xl font-semibold text-gray-900 truncate">{service.service_name}</h2>
 {service.is_featured ? (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-900">
 <Star className="w-3 h-3" /> Featured service
 </span>
 ) : null}
 {service.is_active ? (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
 <CheckCircle className="w-3 h-3" /> Active
 </span>
 ) : (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
 <XCircle className="w-3 h-3" /> Inactive
 </span>
 )}
 </div>
 <p className="text-sm text-gray-600 mt-1 line-clamp-3">{service.description || 'No description'}</p>
 <p className="text-lg font-semibold text-gray-900 mt-3 tabular-nums">{money(service.base_price)}</p>
 </div>
 </div>

 <div className="flex flex-wrap gap-2 shrink-0">
 {vendorProfile?.user_id ? (
 <Link
 to={`/admin/vendors/${vendorProfile.user_id}`}
 className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border text-xs font-medium text-ink hover:bg-surface-muted"
 >
 <ExternalLink className="w-4 h-4" />
 Vendor profile
 </Link>
 ) : null}
 <Button
 variant="danger"
 className="rounded-md"
 disabled={deleteMutation.isPending}
 onClick={() => {
 if (window.confirm('Delete this service? This cannot be undone.')) {
 deleteMutation.mutate(service.id);
 }
 }}
 >
 {deleteMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
 Delete
 </Button>
 </div>
 </div>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
 <section className="product-panel space-y-3 p-4">
 <h3 className="text-sm font-semibold text-gray-900">Controls</h3>
 <div className="product-surface-muted flex items-center justify-between gap-3 p-3">
 <div>
 <p className="text-sm font-medium text-gray-900">Active</p>
 <p className="text-xs text-gray-500 mt-0.5">Clients can book this service when on.</p>
 </div>
 <PillToggle
 checked={Boolean(service.is_active)}
 onChange={() => toggleStatusMutation.mutate(service.id)}
 loading={toggleStatusMutation.isPending}
 variant="success"
 onLabel="On"
 offLabel="Off"
 ariaLabel="Active status"
 />
 </div>
 <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 flex items-center justify-between gap-4">
 <div>
 <p className="text-sm font-medium text-gray-900">Featured service</p>
 <p className="text-xs text-gray-600 mt-0.5">
 Highlights this service in browse results and helps the vendor show on the homepage featured list.
 </p>
 </div>
 <PillToggle
 checked={Boolean(service.is_featured)}
 onChange={() => toggleFeaturedMutation.mutate(service.id)}
 loading={toggleFeaturedMutation.isPending}
 variant="amber"
 onLabel="Yes"
 offLabel="No"
 ariaLabel="Featured service"
 />
 </div>
 {vendorProfile ? (
 <p className="text-xs text-gray-500">
 Vendor homepage flag:{' '}
 <strong className="text-gray-800">
 {vendorProfile.is_featured ? 'Featured vendor' : 'Not featured as vendor'}
 </strong>
 {vendorProfile.user_id ? (
 <>
 {' · '}
 <Link to={`/admin/vendors/${vendorProfile.user_id}`} className="text-ink hover:underline">
 Manage on vendor profile
 </Link>
 </>
 ) : null}
 </p>
 ) : null}
 </section>

 <section className="product-panel space-y-3 p-4">
 <h3 className="text-sm font-semibold text-gray-900">Vendor & category</h3>
 <div className="flex items-start gap-3">
 <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
 <User className="w-5 h-5 text-gray-600" />
 </div>
 <div>
 <p className="text-sm font-medium text-gray-900">
 {vendorProfile?.business_name || vendorUser?.name || 'Unknown vendor'}
 </p>
 <p className="text-xs text-gray-500">{vendorUser?.email || '—'}</p>
 <p className="text-xs text-gray-500 mt-1">
 {[vendorProfile?.city, vendorProfile?.state, vendorProfile?.country].filter(Boolean).join(', ') || 'Location not set'}
 </p>
 </div>
 </div>
 <div className="flex items-start gap-3 pt-2 border-t border-gray-100">
 <Tag className="w-4 h-4 text-gray-400 mt-0.5" />
 <div>
 <p className="text-xs uppercase tracking-wide text-gray-500">Category</p>
 <p className="text-sm font-medium text-gray-900">
 {service.service_category?.category_name || 'Uncategorized'}
 </p>
 </div>
 </div>
 <div className="flex items-start gap-3">
 <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
 <div>
 <p className="text-xs uppercase tracking-wide text-gray-500">Base price</p>
 <p className="text-sm font-medium text-gray-900 tabular-nums">{money(service.base_price)}</p>
 </div>
 </div>
 </section>
 </div>

 <section className="product-panel p-4">
 <h3 className="text-sm font-semibold text-gray-900 mb-3">Packages</h3>
 {packages.length === 0 ? (
 <p className="text-sm text-gray-500">No packages on this service.</p>
 ) : (
 <div className="overflow-x-auto">
 <table className="min-w-full text-sm text-left">
 <thead className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
 <tr>
 <th className="py-2 pr-4 font-semibold">Name</th>
 <th className="py-2 pr-4 font-semibold">Price</th>
 <th className="py-2 font-semibold">Status</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100">
 {packages.map((pkg) => (
 <tr key={pkg.id}>
 <td className="py-2.5 pr-4 text-gray-900">{pkg.package_name || pkg.name || 'Package'}</td>
 <td className="py-2.5 pr-4 tabular-nums text-gray-800">
 {money(pkg.base_price ?? pkg.package_price ?? pkg.price)}
 </td>
 <td className="py-2.5">
 {pkg.is_active ? (
 <span className="text-xs font-semibold text-emerald-700">Active</span>
 ) : (
 <span className="text-xs font-semibold text-gray-500">Inactive</span>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </section>
 </div>
 );
};

export default AdminVendorServiceDetails;
